import { Identifiable, PointArray } from '@editor/interfaces/attribute'
import { ShapeDetectionResult } from '@editor/interfaces/shape'
import { PendingJobResponse } from '@editor/interfaces/system'
import { ERROR_PROCESS } from '@editor/services/ErrorHandler'
import { withStandardHandlers } from '@editor/services/Util'
import { LocalStorageManager } from '@editor/services/localStorage'
import { API_GATEWAY_URL } from '@infraloop/services'
import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { Vector3Like } from 'three'

import { CRACK_DETECTION_POLYGON_ID } from '../constants'
import { CrackDetectionStage, DetectionKind } from '../interfaces'

export const WORKING_ID = CRACK_DETECTION_POLYGON_ID

export interface CrackDetectionResponse {
  url_pcd: string
  path_entwine: string
}

/**
 * One polyline as the chalk detector returns it. Unlike crack detection, which hands back a
 * point cloud for the client to extract lines from, chalk detection returns the lines itself.
 */
export interface ChalkPolyline {
  /** Detector-space to world transformation. Absent means the vertices are already in world space. */
  registration_result?: ShapeDetectionResult['registration_result']

  parameters_polyline: {
    /** Vertices in detector space, as `[x, y, z]` triples. */
    vertices: PointArray[]

    /** Always null — chalk width is entered by hand. */
    width: number | null
  }
}

export interface ChalkDetectionResponse {
  polylines: ChalkPolyline[]
}

/**
 * Detect cracks — or chalk marks — in the specified area.
 *
 * The endpoint switches mode on the presence of `point_for_target_color`: omit it and this is
 * crack detection, send it and the detector looks up the colour under that point and hunts for
 * chalk of that colour instead.
 *
 * @param accessToken Auth0 access token
 * @param projectId Project ID
 * @param areaId Area ID
 * @param positions Array of positions for crack detection
 * @param bounds Bounds information for the detection area
 * @param meshFileNames Optional mesh file names
 * @param pointForTargetColor Point on the mesh whose colour to look for. Switches to chalk detection.
 */
export const detectCracks = createAsyncThunk(
  'polyline/crackDetection/detectCracks',
  async ({
    accessToken,
    projectId,
    areaId,
    positions,
    bounds,
    meshFileNames,
    pointForTargetColor,
  }: {
    accessToken: string
    projectId: string
    areaId: string
    positions: Vector3Like[]
    bounds: {
      center: [number, number, number]
      rotation: [number, number, number, number, number, number, number, number, number]
      extent: [number, number, number]
    }
    meshFileNames?: string[]
    pointForTargetColor?: Vector3Like
  }) => {
    const fn = () =>
      fetch(`${API_GATEWAY_URL}/detect-cracks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          responseType: 'json',
          'X-Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          project_id: projectId,
          inspection_area_id: areaId,
          positions,
          bounds,
          ...(meshFileNames ? { mesh_file_names: meshFileNames } : {}),

          // The endpoint's schema is strict, so the key must be absent rather than undefined.
          ...(pointForTargetColor ? { point_for_target_color: pointForTargetColor } : {}),
        }),
      })

    return withStandardHandlers<typeof fn, PendingJobResponse>(fn, ERROR_PROCESS.DETECT_CRACKS)()
  },
)

export interface CrackDetectionResult extends Identifiable {
  /**
   * Working points for polygon drawing.
   */
  points: PointArray[]

  /**
   * What this run looks for. Absent on runs persisted before chalk detection existed,
   * which were all crack runs — read it through `getDetectionKind`.
   */
  detectionKind?: DetectionKind

  /**
   * Crack detection job token.
   */
  jobToken?: string

  /**
   * Point cloud URL generated from crack detection.
   */
  pcdURL?: string

  /**
   * Path entwine for crack detection result.
   */
  pathEntwine?: string

  /**
   * Chalk detection only: the point the user clicked on the mesh, whose colour the detector
   * should look for. Its presence is also what tells the tool the colour pick is done.
   */
  pointForTargetColor?: PointArray

  /**
   * Chalk detection only: colour sampled from the mesh texture under `pointForTargetColor`,
   * shown back to the user as an approximation. Never sent to the server.
   */
  targetColorHex?: string

  /**
   * Chalk detection only: set once the run has produced polylines. Chalk results have no point
   * cloud, so this is the chalk counterpart of `pcdURL` for "this run produced a result".
   */
  hasChalkResult?: boolean

  /**
   * Current status of crack detection processing.
   */
  status: CrackDetectionStage
}

/**
 * Kind of a run, defaulting runs persisted before chalk detection existed to crack.
 *
 * @param execution Detection execution.
 */
export const getDetectionKind = (execution?: CrackDetectionResult): DetectionKind =>
  execution?.detectionKind ?? DetectionKind.Crack

/**
 * Whether a run has produced a result. Crack runs prove it with a point cloud; chalk runs get
 * their polylines straight from the job, so they carry an explicit flag instead.
 *
 * @param execution Detection execution.
 */
export const hasDetectionResult = (execution?: CrackDetectionResult): boolean =>
  Boolean(execution?.pcdURL) || Boolean(execution?.hasChalkResult)

/**
 * A blank execution to replace an existing one with. The detection kind carries over: resetting a
 * run clears the work, it does not change what the user asked to look for — and the tool can still
 * be sitting in detection mode when this happens.
 *
 * @param previous Execution being replaced, if any.
 */
const blankExecution = (previous?: CrackDetectionResult): CrackDetectionResult => ({
  id: crypto.randomUUID(),
  points: [],
  status: CrackDetectionStage.None,
  detectionKind: previous?.detectionKind,
})

/**
 * Crack detection executions mapped by area ID.
 */
export type CrackDetectionExecution = Record<string, CrackDetectionResult>

const lsExecutions = new LocalStorageManager<CrackDetectionExecution>('editor.polyline.crackDetection.executions', {})

export interface CrackDetectionState {
  /**
   * Current inspection area ID.
   */
  areaId: string

  /**
   * Crack detection executions mapped by area ID.
   */
  executions: CrackDetectionExecution

  /**
   * Backup of the last successful execution.
   * Used for restoring if user entered crack detection mode but decides to cancel.
   */
  backupExecution?: CrackDetectionResult
}

const initialState: CrackDetectionState = {
  areaId: '',
  executions: lsExecutions.load(),
}

export const slice = createSlice({
  name: 'editor/tools/polyline/crackDetection',
  initialState,
  reducers: {
    setAreaId: (state, action: PayloadAction<string>) => {
      state.areaId = action.payload

      if (!state.executions[state.areaId]) {
        state.executions[state.areaId] = {
          id: crypto.randomUUID(),
          points: [],
          status: CrackDetectionStage.None,
        }
      }

      if (state.executions[state.areaId].points.length) {
        state.executions[state.areaId].status = state.executions[state.areaId].jobToken
          ? CrackDetectionStage.Polling
          : CrackDetectionStage.Preview
      }
    },

    // ## Polygon drawing
    addWorkingPoint: (state, action: PayloadAction<PointArray>) => {
      if (!state.executions[state.areaId]) {
        state.executions[state.areaId] = {
          id: crypto.randomUUID(),
          points: [],
          status: CrackDetectionStage.Drawing,
        }
      }

      state.executions[state.areaId].points.push(action.payload)
    },
    updateWorkingPoints: (state, action: PayloadAction<PointArray[]>) => {
      const execution = state.executions[state.areaId]
      if (!execution) return

      execution.points = action.payload

      // The target colour is extracted from the mesh cropped to this polygon, so a moved vertex
      // can leave the picked point outside the detection area. Make the user pick again.
      execution.pointForTargetColor = undefined
      execution.targetColorHex = undefined
    },
    completeDrawing: (state) => {
      if (state.executions[state.areaId]) state.executions[state.areaId].status = CrackDetectionStage.Preview
    },
    undoLastWorkingPoint: (state) => {
      const execution = state.executions[state.areaId]
      if (!execution?.points.length) return

      execution.status = CrackDetectionStage.Drawing
      execution.points.pop()

      // Same reasoning as `updateWorkingPoints` — the polygon changed, so the pick is stale.
      execution.pointForTargetColor = undefined
      execution.targetColorHex = undefined
    },
    initWorking: (state, action: PayloadAction<DetectionKind>) => {
      if (hasDetectionResult(state.executions[state.areaId])) {
        state.backupExecution = { ...state.executions[state.areaId] }
        state.executions[state.areaId] = blankExecution(state.executions[state.areaId])
      }

      const execution = state.executions[state.areaId]
      if (!execution) return

      // Starting the other kind on a polygon that is still being set up invalidates the pick.
      if (getDetectionKind(execution) !== action.payload) {
        execution.pointForTargetColor = undefined
        execution.targetColorHex = undefined
      }
      execution.detectionKind = action.payload

      if (execution.points.length) {
        execution.status =
          execution.jobToken && execution.status !== CrackDetectionStage.Failed
            ? CrackDetectionStage.Polling
            : CrackDetectionStage.Preview
      } else {
        execution.status = CrackDetectionStage.Drawing
      }
    },
    cancelWorking: (state) => {
      // If we are not detecting or fine-tuning, clear working points
      if (
        ![CrackDetectionStage.Polling, CrackDetectionStage.FineTune].includes(state.executions[state.areaId]?.status) &&
        !hasDetectionResult(state.executions[state.areaId])
      ) {
        // clear and re-save executions
        state.executions[state.areaId] = blankExecution(state.executions[state.areaId])
        lsExecutions.save(state.executions)
      }

      // If the run already produced a result, set drawing stage back to FineTune
      if (hasDetectionResult(state.executions[state.areaId])) {
        state.executions[state.areaId].status = CrackDetectionStage.FineTune
      }
    },

    // ## Backup
    restoreBackupExecution: (state) => {
      if (state.backupExecution) {
        state.executions[state.areaId] = { ...state.backupExecution }
        state.backupExecution = undefined
      }
    },

    // ## Detection state
    setDrawingStage: (state, action: PayloadAction<CrackDetectionStage>) => {
      if (state.executions[state.areaId]) state.executions[state.areaId].status = action.payload
    },

    // ## Target colour pick (chalk detection)
    setPointForTargetColor: (state, action: PayloadAction<{ point: PointArray; colorHex?: string }>) => {
      const execution = state.executions[state.areaId]
      if (!execution) return

      execution.pointForTargetColor = action.payload.point
      execution.targetColorHex = action.payload.colorHex

      // Persist so the pick (and the polygon it belongs to) survives a reload.
      lsExecutions.save(state.executions)
    },
    clearPointForTargetColor: (state) => {
      const execution = state.executions[state.areaId]
      if (!execution) return

      execution.pointForTargetColor = undefined
      execution.targetColorHex = undefined
      lsExecutions.save(state.executions)
    },

    // ## Job token
    clearJobToken: (state) => {
      if (
        (hasDetectionResult(state.executions[state.areaId]) ||
          state.executions[state.areaId]?.status === CrackDetectionStage.Failed) &&
        state.executions[state.areaId]
      ) {
        state.executions[state.areaId].jobToken = undefined
      }

      // re-save executions
      lsExecutions.save(state.executions)
    },

    // ## Set point cloud URLs
    setCrackDetectionResult: (state, action: PayloadAction<{ pcdURL?: string; pathEntwine?: string }>) => {
      if (state.executions[state.areaId]) {
        state.executions[state.areaId].pcdURL = action.payload.pcdURL
        state.executions[state.areaId].pathEntwine = action.payload.pathEntwine
        state.executions[state.areaId].status = CrackDetectionStage.FineTune
        state.backupExecution = undefined
      }
    },

    /**
     * Chalk counterpart of `setCrackDetectionResult`. There is no point cloud to record, so this
     * only marks that the run produced a result — the polylines themselves already went into the
     * polyline slice.
     */
    setChalkDetectionResult: (state) => {
      const execution = state.executions[state.areaId]
      if (!execution) return

      execution.hasChalkResult = true
      execution.status = CrackDetectionStage.FineTune
      state.backupExecution = undefined
    },

    // ## Resets
    resetCurrent: (state) => {
      // clear and re-save executions
      state.executions[state.areaId] = blankExecution(state.executions[state.areaId])
      lsExecutions.save(state.executions)
    },

    // ## Special cleanup for tool change as not everything will be reset depending on conditions
    toolChangeCleanup: (state) => {
      // Nothing changes during detecting stage
      if (
        [
          CrackDetectionStage.Requesting,
          CrackDetectionStage.Polling,
          CrackDetectionStage.FineTune,
          CrackDetectionStage.SavingPolylines,
        ].includes(state.executions[state.areaId]?.status)
      )
        return

      // Otherwise, reset everything
      state.executions[state.areaId] = blankExecution(state.executions[state.areaId])
      lsExecutions.save(state.executions)
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(detectCracks.pending, (state) => {
        state.executions[state.areaId].status = CrackDetectionStage.Requesting
      })
      .addCase(detectCracks.fulfilled, (state, action) => {
        if (!state.executions[state.areaId]) return

        state.executions[state.areaId].jobToken = action.payload.job_token
        state.executions[state.areaId].status = CrackDetectionStage.Polling
        lsExecutions.save(state.executions)
      })
      .addCase(detectCracks.rejected, (state) => {
        // clear and re-save executions
        state.executions[state.areaId] = blankExecution(state.executions[state.areaId])
        lsExecutions.save(state.executions)
      })
  },
})

export const {
  setAreaId,

  // ## Polygon drawing
  addWorkingPoint,
  updateWorkingPoints,
  completeDrawing,
  undoLastWorkingPoint,

  initWorking,
  cancelWorking,

  // ## Target colour pick (chalk detection)
  setPointForTargetColor,
  clearPointForTargetColor,

  // ## Detection state
  setDrawingStage,

  // ## Job execution
  clearJobToken,
  restoreBackupExecution,

  // ## Set point cloud URLs
  setCrackDetectionResult,
  setChalkDetectionResult,

  // ## Reset
  toolChangeCleanup,
  resetCurrent,
} = slice.actions

export default slice.reducer
